import React, {useState} from 'react';
import {Box, Typography, Chip, Button, IconButton, Menu, MenuItem, ListItemText, Divider} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useNavigate} from 'react-router-dom';
import {buildGuideUrl} from '../utils/urls';

interface GuideListRowProps {
  guide: {
    id: string;
    orgSlug: string;
    slug: string;
    title: string;
    description: string;
    publishedAt?: string | null;
    steps: {id: string; order: number; title: string}[];
  };
  onPublish?: (guideId: string) => void;
  onUnpublish?: (guideId: string) => void;
  onDelete?: (guideId: string) => void;
}

export default function GuideListRow({guide, onPublish, onUnpublish, onDelete}: GuideListRowProps) {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleClick = () => {
    navigate(`/org/guides/${guide.id}/edit`);
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewPublic = () => {
    handleMenuClose();
    window.open(`/#${buildGuideUrl(guide.orgSlug, guide.slug)}`, '_blank');
  };

  const handleUnpublish = () => {
    handleMenuClose();
    onUnpublish?.(guide.id);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (window.confirm(`Delete "${guide.title}"? This cannot be undone.`)) {
      onDelete?.(guide.id);
    }
  };

  const isDraft = !guide.publishedAt;

  return (
    <Box
      onClick={handleClick}
      sx={{
        px: 3,
        py: 2.5,
        cursor: 'pointer',
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': {borderBottom: 'none'},
        '&:hover': {bgcolor: 'action.hover'},
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <ArticleIcon sx={{color: 'primary.main', fontSize: 28}} />
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
          variant="subtitle1"
          sx={{fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
        >
          {guide.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
        >
          {guide.description}
        </Typography>
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
        <Chip
          label={`${guide.steps.length} steps`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={isDraft ? 'Draft' : 'Published'}
          size="small"
          color={isDraft ? 'default' : 'success'}
          variant={isDraft ? 'outlined' : 'filled'}
        />
        {isDraft && onPublish && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onPublish(guide.id);
            }}
          >
            Publish
          </Button>
        )}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
        >
          <MenuItem onClick={handleViewPublic}>
            <ListItemText>View public guide</ListItemText>
          </MenuItem>
          {!isDraft && onUnpublish && (
            <MenuItem onClick={handleUnpublish}>
              <ListItemText>Unpublish</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleDelete} sx={{color: 'error.main'}}>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
